/**
 * Brand logo components. Swapping the product logo = editing THIS file (and the
 * SVGs in /public/brand/). Everywhere in the app renders these, never a raw
 * icon, so the mark stays consistent and instantly re-brandable.
 *
 * Orlenn mark = the sunburst. The tile is the orange identity gradient with the
 * white mark inside (crisp on any surface, light or dark).
 */
import { cn } from '@shared/lib/utils';
import { BRAND } from './brand';

type MarkSize = 'sm' | 'md' | 'lg';

const SIZES: Record<MarkSize, { box: string; img: string }> = {
  sm: { box: 'h-8 w-8 rounded-lg', img: 'h-5 w-5' },
  md: { box: 'h-9 w-9 rounded-lg', img: 'h-[1.4rem] w-[1.4rem]' },
  lg: { box: 'h-12 w-12 rounded-xl', img: 'h-7 w-7' },
};

/** The square brand icon tile — orange gradient + white sunburst. */
export function BrandMark({
  size = 'sm',
  className,
}: {
  size?: MarkSize;
  className?: string;
}) {
  const s = SIZES[size];
  // The new mark is self-contained (its own light-blue tile), so the container
  // is transparent — no accent background wrapper — and the mark fills the box.
  return (
    <div
      className={cn('flex shrink-0 items-center justify-center overflow-hidden', s.box, className)}
      aria-hidden="true"
    >
      <img
        src="/brand/orlenn-mark.svg"
        alt=""
        draggable={false}
        className="h-full w-full select-none object-contain"
      />
    </div>
  );
}

/** Brand icon + product name (+ optional subtitle). */
export function BrandWordmark({
  subtitle,
  size = 'sm',
  className,
}: {
  subtitle?: string;
  size?: MarkSize;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <BrandMark size={size} />
      <div className="flex flex-col leading-none">
        <span className="text-sm font-bold tracking-tight">{BRAND.name}</span>
        {subtitle && (
          <span className="text-[10px] text-muted-foreground">{subtitle}</span>
        )}
      </div>
    </div>
  );
}
